import React from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '../../components/Icon';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';

import {
  Button as DskButton,
  IconButton as DskIconButton,
  OptionRow as DskOptionRow,
  Pill as DskPill,
  Radio as DskRadio,
} from '../../components/desktop';
import {
  Button as DsButton,
  IconButton as DsIconButton,
  OptionRow as DsOptionRow,
  StatusPill,
  Tag,
  SectionHead,
  SectionTitle,
} from '../../components/mobile';

import './settings.css';

/**
 * The stacked section card. A4's building block, and B7's whole shape.
 *
 * WHY THIS IS A PATTERN AND NOT A PAGE-LOCAL COMPONENT
 * ---------------------------------------------------
 * `/profile` renders this block EIGHT times and `/admin-system-settings`
 * renders it twice more. That is the promotion test in `patterns/README.md`
 * met on its first clause, and it is the reason `desktop/system.css` already
 * carries `.dsk-panel` upstream: the library promoted the CSS, so this layer
 * must not re-invent it, only choose the track and hand it the props.
 *
 * WHY THE UPSTREAM `settings.html` TEMPLATE IS NOT USED
 * ----------------------------------------------------
 * `settings.html` is a TWO COLUMN page: a left settings nav beside a form with
 * a save bar. Neither of this batch's routes is that page. `/profile` is a
 * single stacked column of eight independent sections, each with its own icon,
 * its own `h2` and at most one trailing outline button, and it has no save bar
 * at all because each section commits through its own modal. Adopting the
 * template's chrome would mean inventing a nav for destinations that do not
 * exist and a save button for a form that does not exist. So the template's
 * SHAPE is rejected and its PANEL is kept, which is the part the library
 * promoted.
 *
 * THE ONE ACTION RULE
 * -------------------
 * A panel head takes at most one trailing action. Two buttons in a section
 * header and the section stops being a section and becomes a toolbar, and the
 * page loses the scan line that makes eight of them readable. That rule is
 * stated in `system.css` at `.dsk-panel__head` and it is enforced here by the
 * prop being singular. Anything else goes in the body.
 *
 * THE 1024 HANDOFF
 * ----------------
 * A card is the desktop answer to "this is one of several sections". On a phone
 * eight nested cards inside a scrolling column is just eight borders, so the
 * mobile track's own answer is a section HEAD on the page ground with the
 * content flowing under it. Real DOM difference, so `useViewport()` picks one.
 */

/* ------------------------------------------------------------------- stack */

/** The column. Eight of these down one page on `/profile`. */
export function PanelStack({ className, ...rest }) {
  const { isDesktop } = useViewport();
  return (
    <div
      className={cn(isDesktop ? 'dsk-panel-stack' : 'dsa-set__stack', className)}
      {...rest}
    />
  );
}

/* ------------------------------------------------------------------- panel */

/**
 * One section.
 *
 * @param {{
 *   icon?: object,
 *   title: string,
 *   sub?: string,
 *   action?: React.ReactNode,
 *   flush?: boolean,
 *   quiet?: boolean,
 *   children: React.ReactNode
 * }} props
 */
export function Panel({
  icon,
  title,
  sub,
  action,
  flush = false,
  quiet = false,
  children,
  className,
  ...rest
}) {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return (
      <section
        className={cn(
          'dsk-panel',
          flush && 'dsk-panel--flush',
          quiet && 'dsk-panel--quiet',
          className,
        )}
        {...rest}
      >
        <div className="dsk-panel__head">
          {icon ? (
            <span className="dsk-panel__icon">
              <Icon as={icon} size="lg" />
            </span>
          ) : null}
          <div className="dsk-panel__heading">
            <h2 className="dsk-panel__title">{title}</h2>
            {sub ? <p className="dsk-panel__sub">{sub}</p> : null}
          </div>
          {action ? <div className="dsk-panel__action">{action}</div> : null}
        </div>
        <div className="dsk-panel__body">{children}</div>
      </section>
    );
  }

  return (
    <section className={cn('dsa-set__section', className)} {...rest}>
      <SectionHead>
        <SectionTitle>{title}</SectionTitle>
        {action}
      </SectionHead>
      {sub ? <p className="dsa-set__sub">{sub}</p> : null}
      <div className="dsa-set__body">{children}</div>
    </section>
  );
}

/**
 * The single trailing command in a panel head.
 *
 * Ghost on both tracks, always. The primary command on a settings page belongs
 * to the dialog the button opens, not to the header row, and eight ink buttons
 * down one column would make every section look like the important one.
 */
export function PanelAction({ to, children, className, ...rest }) {
  const { isDesktop } = useViewport();

  // A destination is a LINK, not a button that navigates. That is what makes
  // "See all orders" middle-clickable, and it is why `to` is a real prop here
  // rather than an `onClick` that calls `navigate`.
  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          isDesktop ? 'dsk-btn dsk-btn--ghost dsk-btn--sm' : 'ds-btn ds-btn--ghost ds-btn--sm',
          className,
        )}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  const Control = isDesktop ? DskButton : DsButton;
  return (
    <Control variant="ghost" size="sm" className={className} {...rest}>
      {children}
    </Control>
  );
}

/**
 * The glyph-only command on a row: edit, delete.
 *
 * `label` is mandatory and must name the ROW, not the verb, because
 * `aria-label` replaces the button's contents entirely and eight identical
 * "Edit" buttons down a list is eight identical announcements.
 *
 * There is no destructive variant. A delete is this button plus a real
 * confirmation, and the consequence is carried by the confirmation's label.
 */
export function PanelRowAction({ icon, label, ...rest }) {
  const { isDesktop } = useViewport();

  // The desktop control takes `label` and puts it on `aria-label` itself; the
  // mobile one has no such prop and would land a stray `label` attribute on the
  // button, so each track is given the shape it actually accepts.
  if (isDesktop) {
    return (
      <DskIconButton label={label} {...rest}>
        <Icon as={icon} size="sm" />
      </DskIconButton>
    );
  }

  return (
    <DsIconButton surface="bare" aria-label={label} {...rest}>
      <Icon as={icon} size="sm" />
    </DsIconButton>
  );
}

/* -------------------------------------------------------------------- rows */

/** The list wrapper inside a panel body: addresses, payment methods, orders. */
export function PanelRows({ className, ...rest }) {
  return <div className={cn('dsa-set__rows', className)} {...rest} />;
}

/**
 * One row: an optional leading glyph, a title line that can carry a badge, a
 * muted detail line, and trailing actions.
 *
 * Hover is a reveal and never the only path, so the trailing actions are always
 * in the DOM and always focusable; the CSS only quiets them until the row is
 * hovered or something inside it has focus.
 */
export function PanelRow({ icon, title, badge, detail, actions, className, ...rest }) {
  return (
    <div className={cn('dsa-set__row', className)} {...rest}>
      {icon ? (
        <span className="dsa-set__row-icon">
          <Icon as={icon} size="lg" />
        </span>
      ) : null}
      <div className="dsa-set__row-main">
        <div className="dsa-set__row-title">
          <span className="dsa-set__row-name">{title}</span>
          {badge}
        </div>
        {detail ? <p className="dsa-set__row-detail">{detail}</p> : null}
      </div>
      {actions ? <div className="dsa-set__row-actions">{actions}</div> : null}
    </div>
  );
}

/**
 * The inert marker on a row: "Default", "Verified", "Payout requested".
 *
 * State, never a command, so it is a span on both tracks. `default` is the
 * neutral marker and it maps to the mobile `Tag` rather than a `StatusPill`,
 * because the mobile status tones are all outcome colours and "Default address"
 * is not an outcome.
 */
export function PanelBadge({ tone = 'default', children }) {
  const { isDesktop } = useViewport();
  if (isDesktop) return <DskPill tone={tone}>{children}</DskPill>;
  if (tone === 'default' || tone === 'ink' || tone === 'primary') {
    return <Tag>{children}</Tag>;
  }
  return <StatusPill tone={tone}>{children}</StatusPill>;
}

/* ----------------------------------------------------------------- options */

/**
 * A pick-one list inside a panel body: which saved address is the delivery one.
 *
 * A real radio group, not a list of clickable divs. The old version was divs
 * with `onClick` and an inline `cursor: pointer`, which meant the current
 * choice was announced to nobody and the keyboard could not move between them.
 *
 * Selection is STATE, so the chosen row is an accent tint on both tracks and
 * never an ink fill.
 */
export function PanelOptions({ label, className, ...rest }) {
  const { isDesktop } = useViewport();
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(isDesktop ? 'dsk-options' : 'dsa-set__options', className)}
      {...rest}
    />
  );
}

export function PanelOption({ name, checked, disabled, onChange, title, sub }) {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return (
      <DskOptionRow
        control={
          <DskRadio name={name} checked={checked} disabled={disabled} onChange={onChange} />
        }
        title={title}
        sub={sub}
      />
    );
  }

  return (
    <DsOptionRow
      control="radio"
      disabled={disabled}
      inputProps={{ name, checked, onChange }}
      label={
        <>
          <span className="dsa-set__opt-title">{title}</span>
          {sub ? <span className="dsa-set__opt-sub">{sub}</span> : null}
        </>
      }
    />
  );
}

/* -------------------------------------------------------------------- facts */

/**
 * A definition list of read-only facts: store name, shipping policy, the fields
 * a section shows but does not edit inline.
 *
 * A real `<dl>`, so the pairing is in the semantics rather than only in the
 * columns, which is what makes it readable when the layout collapses to one
 * column on a phone.
 */
export function PanelFacts({ items = [], className }) {
  return (
    <dl className={cn('dsa-set__facts', className)}>
      {items.map((item) => (
        <div className="dsa-set__fact" key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value == null || item.value === '' ? 'Not set' : item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The muted advisory line inside a panel body, e.g. why a command is off. */
export function PanelNote({ icon, children, className }) {
  return (
    <p className={cn('dsa-set__note', className)}>
      {icon ? <Icon as={icon} size="sm" /> : null}
      <span>{children}</span>
    </p>
  );
}

export default Panel;
